// hooks/use-scheduled-countdown.ts
"use client";

import { useState, useEffect, useMemo } from "react";

export function isScheduledPublishPending(scheduledPublishAt: string | null | undefined): boolean {
  if (!scheduledPublishAt) return false;
  const t = new Date(scheduledPublishAt).getTime();
  return !Number.isNaN(t) && t > Date.now();
}

/**
 * Hook pour vérifier si un listing programmé est toujours en attente ou si le countdown est terminé
 * @param scheduledPublishAt - Date ISO string de publication programmée
 * @returns Object avec isScheduled (si le listing est encore programmé) et countdownExpired (si le countdown est terminé)
 */
export function useScheduledCountdown(scheduledPublishAt: string | null | undefined) {
  const [countdownExpired, setCountdownExpired] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const initialIsScheduled = useMemo(
    () => isScheduledPublishPending(scheduledPublishAt),
    [scheduledPublishAt],
  );

  // Démarrer la vérification en temps réel après le montage
  useEffect(() => {
    setIsMounted(true);
    
    if (!scheduledPublishAt) {
      setCountdownExpired(false);
      return;
    }

    const target = new Date(scheduledPublishAt).getTime();
    
    // Vérifier immédiatement si le temps est déjà écoulé
    if (target <= Date.now()) {
      setCountdownExpired(true);
      return;
    }

    setCountdownExpired(false);

    const checkInterval = setInterval(() => {
      const now = Date.now();
      if (target <= now) {
        setCountdownExpired(true);
        clearInterval(checkInterval);
      }
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [scheduledPublishAt]);

  // isScheduled est true seulement si initialement programmé ET que le countdown n'est pas encore terminé
  const isScheduled = initialIsScheduled && !countdownExpired;

  return {
    isScheduled,
    countdownExpired,
    isMounted,
  };
}

